import type { StoreObject } from "@apollo/client";
import { useEffect, useState } from "react";
import RateOptions from "@/components/rate-options";
import SignupModal from "@/components/signup-modal";
import useLeaveAllEscapesMutation from "@/graphql/mutations/use-leave-all-escapes";
import useRemoveSavedGameMutation from "@/graphql/mutations/use-remove-saved-game";
import useSaveGameMutation from "@/graphql/mutations/use-save-game";
import useUpdateEscapeMutation, {
  convertEscapeToInput,
  type EscapeType,
  PrivacyEnum,
} from "@/graphql/mutations/use-update-escape";
import { cache } from "@/helpers/apollo";
import useAnalytics from "@/hooks/use-analytics";
import useAuth from "@/hooks/use-auth";
import { useTimeout } from "@/hooks/use-timers";
import { cn } from "@/lib/utils";
import type { OverallRatingEnum } from "@/pages/search/types";
import ButtonWithMenu from "./button-with-menu";
import type { PlayedButtonGameType } from "./types";

export interface PlayedButtonProps {
  className?: string;
  game: PlayedButtonGameType;
  onCompleted?(): void;
  onPlayedButtonFinished?(): void;
  onRating?(rating: keyof typeof OverallRatingEnum): void;
}

export default function PlayedButton({
  className,
  game,
  onCompleted = () => {
    /* No-op */
  },
  onPlayedButtonFinished,
  onRating = () => {
    /* No-op */
  },
}: PlayedButtonProps) {
  const [flashText, setFlashText] = useState("");
  const { isUser, mutationContext } = useAuth();
  const [signupModal, setSignupModal] = useState(false);
  const { track } = useAnalytics();

  const { start: resetFlashTextWithTimeout } = useTimeout(
    () => setFlashText(""),
    1000
  );
  const { start: finishWithTimeout } = useTimeout(() => {
    if (typeof onPlayedButtonFinished === "function") {
      onPlayedButtonFinished();
    }
  }, 3000);

  const [leaveAllEscapes] = useLeaveAllEscapesMutation(
    mutationContext({ gameId: game.id })
  );
  const [removeSavedGame] = useRemoveSavedGameMutation(
    mutationContext({ gameId: game.id })
  );
  const [saveGame] = useSaveGameMutation(
    mutationContext({ input: { game: game.id } })
  );
  const [updateEscape, { data: updateEscapeData }] = useUpdateEscapeMutation(
    mutationContext({ escape: { game: game.id } })
  );

  const setFlashTextWithTimer = (text: string) => {
    setFlashText(text);
    resetFlashTextWithTimeout();
  };

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy
  const toggleField = (field: string) => () => {
    if (!isUser) {
      setSignupModal(true);
      return;
    }

    // biome-ignore lint/suspicious/noExplicitAny: apollo sux
    const fields: { [field: string]: (value: any) => any } = {};

    if (field === "isSaved") {
      const saved = !game.isSaved;
      fields.isSaved = () => saved;
      (saved ? saveGame : removeSavedGame)();
      track(saved ? "Game Added to Wishlist" : "Game Removed from Wishlist", {
        game_id: game.id,
        game_name: game.name,
        method: "game_details",
      });
    } else if (field === "isCompleted") {
      const completed = !game.isCompleted;
      fields.isCompleted = () => completed;
      fields.isSaved = () => false;
      fields.overallRating = () => null;

      if (completed) {
        onCompleted();
        setFlashTextWithTimer("How was it?");
        updateEscape();
      } else {
        leaveAllEscapes();
        finishWithTimeout();
      }

      track(completed ? "Experience Logged" : "Experience Removed", {
        game_id: game.id,
        game_name: game.name,
        method: "game_details",
      });
    }

    cache.modify({
      fields,
      id: cache.identify(game as unknown as StoreObject),
    });
  };

  const handleRating = (rating: keyof typeof OverallRatingEnum) => {
    const overallRating = game.overallRating === rating ? undefined : rating;

    if (overallRating) {
      onRating(overallRating);
      setFlashTextWithTimer("Rating submitted, thanks!");
    }

    cache.modify({
      fields: {
        overallRating: () => overallRating as keyof typeof OverallRatingEnum,
      },
      id: cache.identify(game as unknown as StoreObject),
    });

    const escapeData = updateEscapeData?.updateEscape.escape || game.myEscape;
    if (!escapeData) {
      return;
    }

    const variables = convertEscapeToInput(escapeData as EscapeType);

    if (variables.experienceReview) {
      variables.experienceReview.overallRating = overallRating;
    } else {
      variables.experienceReview = {
        overallRating,
        privacy: PrivacyEnum.PUBLIC,
      };
    }

    updateEscape(mutationContext(variables));
    finishWithTimeout();
  };

  useEffect(() => {
    if (updateEscapeData?.updateEscape.escape) {
      cache.modify({
        fields: {
          myEscape: () => updateEscapeData.updateEscape.escape as EscapeType,
        },
        id: cache.identify(game as unknown as StoreObject),
      });
    }
  }, [game, updateEscapeData]);

  return (
    <div className={cn("flex items-center justify-between", className)}>
      <div className="relative">
        {flashText !== "" && (
          <div className="absolute flex size-full animate-in items-center duration-300">
            <p className="whitespace-nowrap font-medium text-14 text-white leading-[1.5em] md:text-16">
              {flashText}
            </p>
          </div>
        )}
        <ButtonWithMenu
          className={cn(
            "hidden",
            flashText === "" && "block animate-in duration-300"
          )}
          isCompleted={game.isCompleted ?? false}
          isSaved={game.isSaved ?? false}
          onCompleted={toggleField("isCompleted")}
          onSaved={toggleField("isSaved")}
        />
      </div>
      {game.isCompleted ? (
        <RateOptions
          onClick={handleRating}
          overallRating={game.overallRating}
        />
      ) : (
        <p className="max-w-27.5 text-right text-12 text-fog leading-[1.5em] md:max-w-none md:text-14">
          Mark as done to rate
        </p>
      )}
      <SignupModal onOpenChange={setSignupModal} open={signupModal} />
    </div>
  );
}
